import Icon from 'components/atoms/Icon';
import { TJobRoleFormValues } from 'components/feature/jobRole/JobRoleForm/JobRoleForm.types';
import { orientationOptions } from 'components/feature/jobRole/TraitsFormItem/TraitsFormItem';
import Decimal from 'decimal.js';
import { useField, useFormikContext } from 'formik';
import {
  Orientation,
  SoftSkillFragment,
  useSoftSkillFindManyLazyQuery,
  useSoftSkillFindManyPaginatedQuery,
} from 'generated/graphql';
import { debounce, isNumber } from 'lodash';
import uniqBy from 'lodash/uniqBy';
import { FC, useEffect, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';
import {
  IFormikSelectFormField,
  SelectFormField,
  TNotification,
  TSelectOption,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

export const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_TIME = 500;

type PaginationStateType = Record<
  string,
  { currentPage?: number; totalPages?: number }
>;

export type SoftSkillOption = TSelectOption & {
  additionalProps?: SoftSkillFragment;
};
type OptionsState = Record<string, SoftSkillOption[]>;

type UseGetSoftSkillOptionsProps = {
  companyId: number;
  predefinedSoftSkills?: SoftSkillFragment[];
  initialValue?: string;
};

const useGetSoftSkillOptions = ({
  companyId,
  predefinedSoftSkills,
  initialValue,
}: UseGetSoftSkillOptionsProps) => {
  const { handleMsgType } = useNotification();
  const [options, setOptions] = useState<OptionsState>({
    '': [],
  });
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationStateType>({
    '': {
      currentPage: 1,
      totalPages: undefined,
    },
  });
  const [searchValue, setSearchValue] = useState('');

  const [softSkillsFindMany] = useSoftSkillFindManyLazyQuery();
  useEffect(() => {
    const getInitialSoftSkills = async () => {
      try {
        const parsedInitialValue = isNumber(Number(initialValue))
          ? Number(initialValue)
          : undefined;
        const initialSoftSkill = predefinedSoftSkills?.find(
          softSkill => softSkill.id === parsedInitialValue
        );
        let initialOptions: SoftSkillOption[] = [];

        if (!initialSoftSkill && parsedInitialValue) {
          const initialSoftSkillsQueryRes = await softSkillsFindMany({
            variables: {
              ids: [parsedInitialValue],
            },
          });

          if (initialSoftSkillsQueryRes.data?.SoftSkillFindMany) {
            initialOptions = [
              ...getOptionsFromSoftSkills(
                initialSoftSkillsQueryRes.data
                  .SoftSkillFindMany as SoftSkillFragment[]
              ),
            ];
          }
        }

        if (predefinedSoftSkills) {
          initialOptions = [
            ...initialOptions,
            ...getOptionsFromSoftSkills(predefinedSoftSkills),
          ];
        }

        setOptions({
          '': [...(options[''] || []), ...initialOptions],
        });
        setLoading(false);
      } catch (error) {
        handleMsgType({
          type: TNotification.error,
          message: (error as ApolloError)?.message,
        });
        setLoading(false);
      }
    };

    getInitialSoftSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSoftSkillFindManyPaginatedQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      filters: {
        companyId: companyId,
        name: searchValue,
      },
      pagination: {
        page: pagination[searchValue]?.currentPage
          ? pagination[searchValue].currentPage
          : 1,
        size: PAGE_SIZE,
      },
    },
    onCompleted: data => {
      const resultSkills = (data?.SoftSkillFindManyPaginated.data ||
        []) as SoftSkillFragment[];
      const optionsFromResult = getOptionsFromSoftSkills(resultSkills);
      const mergedOptions = mergePreviousAndNewOptions({
        newOptions: optionsFromResult,
        prevOptions: options[searchValue] || [],
      });
      const mergedOptionsWithInitialOptions = mergePreviousAndNewOptions({
        newOptions: optionsFromResult,
        prevOptions: options[''] || [],
      });
      setOptions(prevOptions => ({
        ...prevOptions,
        [searchValue]: mergedOptions,
        '': mergedOptionsWithInitialOptions,
      }));

      const pageInfo = data.SoftSkillFindManyPaginated.pageInfo;

      setPagination(prevPagination => ({
        ...prevPagination,
        [searchValue]: {
          currentPage: pageInfo?.currentPage,
          totalPages: pageInfo?.pageTotal,
        },
      }));

      setLoading(false);
      setPaginationLoading(false);
    },
    onError: error => {
      handleMsgType({ type: TNotification.error, message: error?.message });
      setLoading(false);
      setPaginationLoading(false);
    },
  });

  const search = async (inputValue: string) => {
    if (inputValue === searchValue) return;
    setLoading(true);
    setSearchValue(inputValue);
  };

  const debouncedSearch = debounce(search, SEARCH_DEBOUNCE_TIME);

  const loadMore = async () => {
    setPaginationLoading(true);
    const currentPage = pagination[searchValue]?.currentPage;
    const totalPages = pagination[searchValue]?.totalPages;
    const newPage = currentPage ? currentPage + 1 : 1;
    if (totalPages !== undefined && newPage > totalPages) {
      setPaginationLoading(false);
      return;
    }
    setPagination(prev => ({
      ...prev,
      [searchValue]: {
        currentPage: newPage,
        totalPages: pagination[searchValue]?.totalPages,
      },
    }));
  };

  return {
    options: options[searchValue] || [],
    loading: loading,
    currentPage: pagination[searchValue]?.currentPage,
    totalPages: pagination[searchValue]?.totalPages,
    debouncedSearch,
    paginationLoading,
    loadMore,
  };
};

function getOptionsFromSoftSkills(
  softSkills: SoftSkillFragment[]
): SoftSkillOption[] {
  return softSkills.map(softSkill => ({
    value: `${softSkill.id}`,
    label: softSkill.name,
    labelNote: softSkill.description || '',
    labelDescription: `v${softSkill.taxonomyVersion}`,
    labelIcon: softSkill.company ? <Icon icon="apartment" /> : undefined,
    additionalProps: softSkill,
  }));
}

function mergePreviousAndNewOptions({
  prevOptions,
  newOptions,
}: {
  prevOptions: SoftSkillOption[];
  newOptions: SoftSkillOption[];
}): SoftSkillOption[] {
  return uniqBy([...prevOptions, ...newOptions], 'value');
}

export interface IFormikSoftSkillNameSelectFormField
  extends Omit<IFormikSelectFormField, 'options' | 'useFormikField'> {
  companyId: string;
  predefinedSoftSkills?: SoftSkillFragment[];
  index: number;
}

export const FormikSoftSkillNameSelectFormField: FC<IFormikSoftSkillNameSelectFormField> =
  ({ companyId, id, predefinedSoftSkills, index, ...rest }) => {
    const companyIdNumber = Number(companyId);

    const [field, meta] = useField<SoftSkillOption>(id);
    const { values: formValues, setFieldValue } =
      useFormikContext<TJobRoleFormValues>();

    const { options, loading, debouncedSearch, paginationLoading, loadMore } =
      useGetSoftSkillOptions({
        companyId: companyIdNumber,
        predefinedSoftSkills,
        initialValue: field.value.value,
      });

    const handleChange = (newValues: SoftSkillOption) => {
      if (newValues.value === field.value.value) return;

      const selectedSoftSkill = newValues.additionalProps as SoftSkillFragment;

      if (!selectedSoftSkill) return;

      // After selecting the soft skill, we update other fields in the row.
      // Soft skill type, weight and traits values are updated.
      const softSkillRowValues = formValues.softSkills[index];

      const newSoftSkillRowTraitsValues =
        selectedSoftSkill.softSkillTraits?.map(ssTrait => ({
          id: ssTrait.id || 0,
          traitId: ssTrait.traitId || 0,
          name: ssTrait.traitName || '',
          weight: new Decimal(ssTrait.weight).mul(100),
          orientation:
            orientationOptions[
              ssTrait.orientation === Orientation.Standard ? '1' : '0'
            ],
        })) || [];

      const newSoftSkillRowValues = {
        softSkillId: newValues,
        name: selectedSoftSkill.name,
        type: {
          value: softSkillRowValues.type.value,
          label: softSkillRowValues.type.label,
        },
        weight: '',
        traitsValues: newSoftSkillRowTraitsValues,
        includeInCalibration: softSkillRowValues.includeInCalibration,
      };

      setFieldValue(`softSkills.${index}`, newSoftSkillRowValues);
    };

    const hasError = Boolean(meta.error && meta.touched);
    const error = meta.error as unknown as
      | { label: string; value: string }
      | undefined;

    const filteredOptions = useMemo(() => {
      // Remove skills that are already selected in other rows from the options.
      const otherSelectedSoftSkillIds = formValues.softSkills
        .map(formValuesSoftSkills => formValuesSoftSkills.softSkillId.value)
        .filter(
          selectedSoftSkillValues =>
            selectedSoftSkillValues &&
            selectedSoftSkillValues !== field.value.value
        );

      return options.filter(
        option => !otherSelectedSoftSkillIds.includes(option.value)
      );
    }, [options, formValues.softSkills, field.value.value]);

    const value = useMemo(
      () => options.find(option => option.value === field.value.value),
      [options, field.value.value]
    );

    return (
      <SelectFormField
        {...rest}
        id={id}
        value={value}
        onChange={handleChange}
        onInputChange={debouncedSearch}
        onMenuScrollToBottom={loadMore}
        options={filteredOptions}
        hasError={hasError}
        bottomText={hasError ? error?.value : ''}
        hasClearIndicator={false}
        isLoading={loading}
        disableFilter
        loadingPaginatedOptions={paginationLoading}
      />
    );
  };
